// src/business/context/BusinessAuthContext.jsx
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { businessApi, request } from "../../services/api";

const BusinessAuthContext = createContext(null);

/**
 * Storage key for business portal session.
 * We keep it separate from shop user auth token.
 */
export const BUSINESS_SESSION_KEY = "business_session";

/** Safe JSON parse */
function safeParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Read session from localStorage */
function readSession() {
  const raw = localStorage.getItem(BUSINESS_SESSION_KEY);
  const parsed = raw ? safeParse(raw) : null;
  if (parsed?.token) return parsed;
  return null;
}

function normalizePermissions(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeMemberships(payload) {
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

  return list
    .map((item) => {
      const id = Number(item?.id);
      if (!Number.isInteger(id) || id <= 0) return null;

      return {
        ...item,
        id,
        membership_id: Number(item?.membership_id || 0) || null,
        role_id: Number(item?.role_id || 0) || null,
        permissions: normalizePermissions(item?.permissions ?? item?.permissions_csv),
      };
    })
    .filter(Boolean);
}

function resolveActiveBusinessId({ requestedId = null, memberships = [], fallbackId = null }) {
  const availableIds = new Set(memberships.map((membership) => Number(membership.id)));
  const normalizedRequested = Number(requestedId);
  const normalizedFallback = Number(fallbackId);

  if (Number.isInteger(normalizedRequested) && availableIds.has(normalizedRequested)) {
    return normalizedRequested;
  }

  if (Number.isInteger(normalizedFallback) && availableIds.has(normalizedFallback)) {
    return normalizedFallback;
  }

  if (memberships.length > 0) {
    return Number(memberships[0].id);
  }

  return null;
}

function buildSessionSnapshot(baseSession, memberships, nextActiveBusinessId) {
  const currentBusiness = memberships.find((membership) => Number(membership.id) === Number(nextActiveBusinessId)) || null;

  return {
    ...baseSession,
    memberships,
    activeBusinessId: nextActiveBusinessId,
    businessId: nextActiveBusinessId,
    businessName: currentBusiness?.name || null,
    role: currentBusiness?.role || null,
    permissions: currentBusiness?.permissions || [],
  };
}

/** Save session to localStorage */
function writeSession(session) {
  localStorage.setItem(BUSINESS_SESSION_KEY, JSON.stringify(session));
}

/** Remove session */
function clearSession() {
  localStorage.removeItem(BUSINESS_SESSION_KEY);
}



/**
 * BusinessAuthProvider
 * - backend-ready (no role UI gating)
 * - frontend stores token + user snapshot
 * - backend decides permissions (403)
 */
export function BusinessAuthProvider({ children }) {
  const [session, setSession] = useState(null); // { token, user, role?, businessId? }
  const [loading, setLoading] = useState(true);
  const sessionRef = useRef(null);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  /** Sync session from storage (useful after refresh, BFCache, multi-tab) */
  const syncSession = useCallback(() => {
    const restored = readSession();
    sessionRef.current = restored;
    setSession(restored);
    setLoading(false);
  }, []);

  const refreshMemberships = useCallback(async (sessionOverride = null) => {
    const currentSession = sessionOverride || readSession() || sessionRef.current;
    if (!currentSession?.token) {
      return [];
    }

    const response = await request({ url: "/api/businesses", method: "GET" });
    const memberships = normalizeMemberships(response);

    setSession((prev) => {
      const base = sessionOverride || prev || currentSession;
      if (!base?.token) return base;

      const nextActiveBusinessId = resolveActiveBusinessId({
        requestedId: base.activeBusinessId ?? base.businessId,
        memberships,
        fallbackId: currentSession?.activeBusinessId ?? currentSession?.businessId,
      });

      const next = buildSessionSnapshot(base, memberships, nextActiveBusinessId);
      sessionRef.current = next;
      writeSession(next);
      return next;
    });

    return memberships;
  }, []);

  useEffect(() => {
    // Initial load
    syncSession();

    // Multi-tab sync
    const onStorage = (e) => {
      if (e.key === BUSINESS_SESSION_KEY) syncSession();
    };

    // BFCache restore
    const onPageShow = () => syncSession();

    // Tab focus → resync
    const onFocus = () => syncSession();

    window.addEventListener("storage", onStorage);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Login (Business portal)
   * Backend can return token + user + optional role/businessId.
   * We store whatever backend returns.
   */
  const login = useCallback(async ({ email, password }) => {
    const res = await businessApi.businessLogin({ email, password });

    const token = res?.token || res?.accessToken || null;
    const user = res?.user || res?.account || res?.admin || null;

    if (!token) throw new Error("Login failed: token not returned.");

    // Save session immediately so the next request is authenticated
    const initial = {
      token,
      user,
      role: null,
      businessId: null,
      activeBusinessId: null,
      businessName: null,
      permissions: [],
      memberships: [],
    };
    writeSession(initial);
    setSession(initial);

    let memberships = [];
    try {
      memberships = await refreshMemberships(initial);
    } catch {
      memberships = [];
    }

    const nextActiveBusinessId = resolveActiveBusinessId({
      requestedId: res?.businessId || res?.data?.businessId || user?.businessId,
      memberships,
    });

    const next = buildSessionSnapshot(initial, memberships, nextActiveBusinessId);
    writeSession(next);
    setSession(next);

    return res;
  }, [refreshMemberships]);

  /**
   * Logout
   * - Clear local session
   * - Try backend logout (safe if backend isn't ready yet)
   */
  const logout = useCallback(async () => {
    clearSession();
    setSession(null);

    try {
      await businessApi.businessLogout();
    } catch {
      // ignore backend failure
    }
  }, []);

  useEffect(() => {
    if (!session?.token) return;

    let cancelled = false;
    const currentSession = sessionRef.current;

    refreshMemberships(currentSession).catch(() => {
      if (!cancelled) {
        // keep the existing session snapshot; caller-facing auth should stay stable
      }
    });

    return () => {
      cancelled = true;
    };
  }, [refreshMemberships, session?.token]);

  const selectBusiness = useCallback(async (businessId) => {
    const memberships = session?.memberships || [];
    const nextActiveBusinessId = resolveActiveBusinessId({
      requestedId: businessId,
      memberships,
    });

    if (!nextActiveBusinessId) {
      throw new Error("Selected business is not available for this account.");
    }

    setSession((prev) => {
      if (!prev) return prev;
      const next = buildSessionSnapshot(prev, memberships, nextActiveBusinessId);
      sessionRef.current = next;
      writeSession(next);
      return next;
    });
  }, [session]);

  const value = useMemo(() => {
    const token = session?.token || null;
    const user = session?.user || null;
    const permissions = session?.permissions || [];
    const memberships = session?.memberships || [];
    const activeBusinessId = session?.activeBusinessId || null;
    const currentBusiness = memberships.find((membership) => Number(membership.id) === Number(activeBusinessId)) || null;

    return {
      loading,
      session,
      token,
      user,
      memberships,
      currentBusiness,
      activeBusinessId,

      // optional metadata
      role: currentBusiness?.role || session?.role || user?.role || null,
      businessId: activeBusinessId,
      businessName: currentBusiness?.name || session?.businessName || null,
      permissions,

      /** Check if the user has a specific permission. */
      hasPermission: (perm) => permissions.includes(perm),

      isAuthenticated: Boolean(token),

      login,
      logout,
      syncSession,
      refreshMemberships,
      selectBusiness,
    };
  }, [loading, login, logout, refreshMemberships, selectBusiness, session, syncSession]);

  return <BusinessAuthContext.Provider value={value}>{children}</BusinessAuthContext.Provider>;
}

/** Hook */
export function useBusinessAuth() {
  const ctx = useContext(BusinessAuthContext);
  if (!ctx) throw new Error("useBusinessAuth must be used inside BusinessAuthProvider");
  return ctx;
}
